import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCheck } from 'react-icons/fa';
import { useHistory } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import CheckoutForm from '../Payment/CheckoutForm';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;
const basicPriceId = process.env.REACT_APP_STRIPE_BASIC_PRICE_ID;

const isConfiguredValue = (value, placeholder) => {
  return Boolean(value) && value !== placeholder && !value.includes('replace_me');
};

const PricingContainer = styled.div`
  padding: 80px 20px;
  background: linear-gradient(to bottom, #f8f9fa, #ffffff);
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 60px 16px;
    padding-bottom: 40px;
  }
`;

const PricingHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  
  h2 {
    font-size: 2.5rem;
    color: #2d3748;
    margin-bottom: 20px;
  }
  
  p {
    font-size: 1.1rem;
    color: #718096;
    line-height: 1.6;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 10px 0;
    width: 100%;
    overflow-x: hidden;
  }
`;

const PricingCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-5px);
  }

  @media (max-width: 768px) {
    padding: 20px;
    margin: 0;
    width: 100%;
    box-sizing: border-box;
  }
  
  &.popular {
    border: 2px solid #4A6CF7;
    position: relative;
    
    &::before {
      content: 'Most Popular';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #4A6CF7;
      color: white;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 0.8rem;
    }
  }
`;

const PlanName = styled.h3`
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 10px;
`;

const Price = styled.div`
  font-size: 2.5rem;
  color: #4A6CF7;
  margin-bottom: 20px;
  
  span {
    font-size: 1rem;
    color: #718096;
  }
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  color: #4a5568;
  
  svg {
    margin-right: 10px;
    color: ${props => props.included ? '#48bb78' : '#cbd5e0'};
  }
`;

const StyledButton = styled.button`
  display: block;
  width: 100%;
  padding: 12px;
  background-color: ${props => props.primary ? '#4A6CF7' : 'white'};
  color: ${props => props.primary ? 'white' : '#4A6CF7'};
  border: 2px solid #4A6CF7;
  border-radius: 8px;
  text-align: center;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.primary ? '#3b5bdb' : '#f7fafc'};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  max-width: 500px;
  width: 90%;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #718096;
  
  &:hover {
    color: #2d3748;
  }
`;

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const history = useHistory();
  const { currentUser } = useAuth();

  const plans = [
    {
      name: 'Free',
      price: '0',
      features: [
        { text: 'Save up to 5 active tasks', included: true },
        { text: 'Random, Start, and Flow modes', included: true },
        { text: 'Completed task history', included: true },
        { text: 'Starter templates', included: true },
        { text: 'Calendar planning and ICS export', included: true },
      ],
      buttonText: 'Get Started',
      primary: false,
      isFree: true
    },
    {
      name: 'Unlimited',
      price: '2',
      priceId: basicPriceId,
      features: [
        { text: 'Unlimited tasks', included: true },
        { text: 'Random, Start, and Flow modes', included: true },
        { text: 'Completed task history', included: true },
        { text: 'Starter templates', included: true },
        { text: 'Calendar planning and ICS export', included: true },
      ],
      buttonText: 'Choose Unlimited',
      primary: true,
      popular: true,
      isFree: false
    },
  ];

  const handlePlanSelect = (plan) => {
    if (plan.isFree) {
      history.push(currentUser ? '/user' : '/signup');
    } else {
      if (!currentUser) {
        history.push('/login');
        return;
      }

      const missingConfig = [];
      if (!isConfiguredValue(stripePublishableKey, 'pk_live_replace_me')) {
        missingConfig.push('REACT_APP_STRIPE_PUBLISHABLE_KEY');
      }
      if (!isConfiguredValue(plan.priceId, 'price_replace_basic')) {
        missingConfig.push('REACT_APP_STRIPE_BASIC_PRICE_ID');
      }

      if (!stripePromise || missingConfig.length > 0) {
        window.alert(`Payments are not configured yet. Missing: ${missingConfig.join(', ')}`);
        return;
      }
      // For paid plans, show the payment modal
      setSelectedPlan(plan);
    }
  };

  const handlePaymentSuccess = () => {
    setSelectedPlan(null);
    history.push('/profile');
  };

  return (
    <PricingContainer className="scroll-container">
      <PricingHeader>
        <h2>Simple, Transparent Pricing</h2>
        <p>Start free with the complete focus workflow. Upgrade only when you need more than five active tasks.</p>
      </PricingHeader>
      
      <PricingGrid>
        {plans.map((plan) => (
          <PricingCard key={plan.name} className={plan.popular ? 'popular' : ''}>
            <PlanName>{plan.name}</PlanName>
            <Price>
              ${plan.price}<span>/month</span>
            </Price>
            <FeatureList>
              {plan.features.map((feature, index) => (
                <FeatureItem key={index} included={feature.included}>
                  <FaCheck />
                  {feature.text}
                </FeatureItem>
              ))}
            </FeatureList>
            <StyledButton 
              type="button"
              onClick={() => handlePlanSelect(plan)}
              primary={plan.primary}
            >
              {plan.buttonText}
            </StyledButton>
          </PricingCard>
        ))}
      </PricingGrid>

      {selectedPlan && !selectedPlan.isFree && (
        <Modal>
          <ModalContent>
            <CloseButton onClick={() => setSelectedPlan(null)}>&times;</CloseButton>
            <Elements stripe={stripePromise}>
              <CheckoutForm 
                plan={selectedPlan.name}
                price={selectedPlan.price}
                priceId={selectedPlan.priceId}
                onSuccess={handlePaymentSuccess}
              />
            </Elements>
          </ModalContent>
        </Modal>
      )}
    </PricingContainer>
  );
};

export default Pricing; 
